import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { notifyError, leaveBreadcrumb } from '@/lib/bugsnag';
import { backendApiUrl } from '@/lib/backend-url';

export interface Business {
  id: string;
  account_id: string;
  user_id?: string;
  name: string;
  description?: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  business_type?: string;
  is_active?: boolean;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountantBusinessAssignment {
  id: string;
  accountant_user_id: string;
  business_id: string;
  permissions: {
    files: boolean;
    integrations: boolean;
    chat: boolean;
  };
  created_at: string;
  updated_at: string;
}

const BUSINESS_KEY = 'business';
const BUSINESSES_KEY = 'businesses';

/**
 * Helper function to check if current user is an accountant
 */
async function checkIsAccountant(supabase: ReturnType<typeof createClient>, userId: string): Promise<boolean> {
  // First check user_metadata
  const { data: { user } } = await supabase.auth.getUser();
  if (user?.user_metadata?.user_type === 'accountant') {
    return true;
  }
  
  // Then check database profile
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_type')
    .eq('user_id', userId)
    .single();
  
  if (error) {
    return false;
  }
  
  return data?.user_type === 'accountant';
}

/**
 * Helper function to get assigned businesses for accountant
 */
async function getAccountantBusinesses(supabase: ReturnType<typeof createClient>, userId: string): Promise<Business[]> {
  // Get assigned business IDs
  const { data: assignments, error: assignmentError } = await supabase
    .from('accountant_business_assignments')
    .select('business_id')
    .eq('accountant_user_id', userId);

  if (assignmentError || !assignments || assignments.length === 0) {
    return [];
  }

  // Get business details
  const businessIds = assignments.map(a => a.business_id);
  const { data: businesses, error: businessError } = await supabase
    .from('businesses')
    .select('*')
    .in('id', businessIds)
    .is('deleted_at', null)
    .order('name');

  if (businessError || !businesses) {
    return [];
  }

  return businesses;
}

// Hook to get business by account ID
export function useBusinessById(accountId: string) {
  const supabase = createClient();

  return useQuery({
    queryKey: [BUSINESS_KEY, accountId],
    queryFn: async (): Promise<Business | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token available');
      }

      // Get business from database
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('account_id', accountId)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No business found for this account
          return null;
        }
        console.error('Business fetch error:', error);
        throw new Error('Failed to fetch business information');
      }

      return data;
    },
    enabled: !!accountId,
  });
}

// Hook to get all businesses for the current user (using user_id)
// For accountant users, this returns assigned businesses instead
export function useUserBusinesses() {
  const supabase = createClient();

  return useQuery({
    queryKey: [BUSINESSES_KEY, 'user'],
    queryFn: async (): Promise<Business[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No user available');
      }

      // Check if user is an accountant
      const isAccountant = await checkIsAccountant(supabase, user.id);
      
      if (isAccountant) {
        // For accountants, return assigned businesses
        return await getAccountantBusinesses(supabase, user.id);
      }

      // For regular users, get businesses they own OR have access to via team membership
      // First get all accounts the user belongs to (includes team accounts)
      const { data: accounts } = await supabase.rpc('get_accounts');
      const accountIds = accounts?.map((acc: any) => acc.account_id) || [];

      let businesses: Business[] | null = null;
      let businessesError: any = null;

      if (accountIds.length > 0) {
        // Query businesses by user_id OR by account membership
        const result = await supabase
          .from('businesses')
          .select('*')
          .or(`user_id.eq.${user.id},account_id.in.(${accountIds.join(',')})`)
          .is('deleted_at', null)
          .order('is_active', { ascending: false })
          .order('created_at', { ascending: false });
        businesses = result.data;
        businessesError = result.error;
      } else {
        // Fallback: just query by user_id
        const result = await supabase
          .from('businesses')
          .select('*')
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .order('is_active', { ascending: false })
          .order('created_at', { ascending: false });
        businesses = result.data;
        businessesError = result.error;
      }

      if (businessesError) {
        console.error('Businesses fetch error:', businessesError);
        return [];
      }

      // Track users with no businesses (potential "setup required" issue)
      if (!businesses || businesses.length === 0) {
        leaveBreadcrumb('User has no businesses', {
          userId: user.id,
          isAccountant: false,
        }, 'state');
        // This is an info-level notification to track the pattern
        notifyError(
          new Error('User has no businesses - setup required'),
          'user_no_businesses',
          { userId: user.id, email: user.email },
          'info'
        );
      }

      return businesses || [];
    },
  });
}

// Hook to get the active business for the current user
// For accountant users, returns the first assigned business or stored preference
export function useActiveBusiness() {
  const supabase = createClient();

  return useQuery({
    queryKey: [BUSINESSES_KEY, 'active'],
    queryFn: async (): Promise<Business | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No user available');
      }

      // Check if user is an accountant
      const isAccountant = await checkIsAccountant(supabase, user.id);
      
      if (isAccountant) {
        // For accountants, get their assigned businesses
        const assignedBusinesses = await getAccountantBusinesses(supabase, user.id);
        
        if (assignedBusinesses.length === 0) {
          return null;
        }
        
        // Check if there's a stored preference in localStorage
        const storedActiveBusinessId = typeof window !== 'undefined' 
          ? localStorage.getItem(`accountant_active_business_${user.id}`)
          : null;
        
        if (storedActiveBusinessId) {
          const storedBusiness = assignedBusinesses.find(b => b.id === storedActiveBusinessId);
          if (storedBusiness) {
            return storedBusiness;
          }
        }
        
        // Return the first assigned business
        return assignedBusinesses[0];
      }

      // For regular users, get active business (owned or via team membership)
      const { data: accounts } = await supabase.rpc('get_accounts');
      const accountIds = accounts?.map((acc: any) => acc.account_id) || [];

      const accessFilter = accountIds.length > 0
        ? `user_id.eq.${user.id},account_id.in.(${accountIds.join(',')})`
        : `user_id.eq.${user.id}`;

      const { data: businesses, error } = await supabase
        .from('businesses')
        .select('*')
        .or(accessFilter)
        .eq('is_active', true)
        .is('deleted_at', null)
        .limit(1);

      if (error) {
        console.error('Active business fetch error:', error);
        return null;
      }

      if (businesses && businesses.length > 0) {
        return businesses[0];
      }

      // If no active business, get the first one
      const { data: firstBusiness } = await supabase
        .from('businesses')
        .select('*')
        .or(accessFilter)
        .is('deleted_at', null)
        .order('created_at')
        .limit(1);

      const activeBusiness = firstBusiness?.[0] || null;
      
      // Track when user has no active business (setup required scenario)
      if (!activeBusiness) {
        leaveBreadcrumb('No active business found', {
          userId: user.id,
          isAccountant: false,
        }, 'state');
      }

      return activeBusiness;
    },
  });
}

// Hook to set a business as active
// For accountants, this stores the preference locally (they don't own the business)
export function useSetActiveBusiness() {
  const supabase = createClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (businessId: string): Promise<Business> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No user available');
      }

      // Check if user is an accountant
      const isAccountant = await checkIsAccountant(supabase, user.id);
      
      if (isAccountant) {
        // For accountants, verify they have access to this business
        const { data: assignment, error: assignmentError } = await supabase
          .from('accountant_business_assignments')
          .select('business_id')
          .eq('accountant_user_id', user.id)
          .eq('business_id', businessId)
          .single();
        
        if (assignmentError || !assignment) {
          throw new Error('You do not have access to this business');
        }
        
        // Store preference in localStorage (accountants can't modify businesses table)
        if (typeof window !== 'undefined') {
          localStorage.setItem(`accountant_active_business_${user.id}`, businessId);
        }
        
        // Fetch and return the business
        const { data: business, error: businessError } = await supabase
          .from('businesses')
          .select('*')
          .eq('id', businessId)
          .single();
        
        if (businessError || !business) {
          throw new Error('Failed to fetch business details');
        }
        
        return business;
      }

      // For regular users, update the database
      // Deactivate all businesses for this user
      await supabase
        .from('businesses')
        .update({ is_active: false })
        .eq('user_id', user.id);

      // Activate the selected business
      const { data, error } = await supabase
        .from('businesses')
        .update({ is_active: true })
        .eq('id', businessId)
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        throw new Error('Failed to set active business');
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate business queries to refetch
      queryClient.invalidateQueries({ queryKey: [BUSINESSES_KEY] });
      queryClient.invalidateQueries({ queryKey: [BUSINESS_KEY] });
    },
  });
}

// Hook to create a new business
export function useCreateBusiness() {
  const supabase = createClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, setActive = true }: { name: string; setActive?: boolean }): Promise<Business> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No user available');
      }

      // Create a proper team/business account (personal_account=false)
      // Do NOT use the personal account — RLS policies require a team account
      const { data: accounts } = await supabase.rpc('get_accounts');
      
      // First check if user already has a non-personal account without a business
      let accountId: string | null = null;
      const teamAccounts = accounts?.filter((acc: any) => !acc.personal_account) || [];
      
      // Check which team accounts already have businesses
      for (const acc of teamAccounts) {
        const { data: existingBiz } = await supabase
          .from('businesses')
          .select('id')
          .eq('account_id', acc.account_id)
          .is('deleted_at', null)
          .limit(1);
        if (!existingBiz || existingBiz.length === 0) {
          // This team account has no business — reuse it
          accountId = acc.account_id;
          break;
        }
      }

      // If no reusable team account, create a new one via the backend API
      if (!accountId) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No session available');
        }
        
        const response = await fetch(backendApiUrl('/create-business'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Api-Key': process.env.NEXT_PUBLIC_ADMIN_API_KEY || '',
          },
          body: JSON.stringify({
            user_id: user.id,
            business_name: name,
            set_active: setActive,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Backend create-business failed:', errorText);
          throw new Error('Failed to create business');
        }

        const result = await response.json();
        return result.business;
      }

      // If we found a reusable team account, create the business directly
      if (setActive) {
        await supabase
          .from('businesses')
          .update({ is_active: false })
          .eq('user_id', user.id);
      }

      const { data, error } = await supabase
        .from('businesses')
        .insert({
          account_id: accountId,
          user_id: user.id,
          name: name,
          created_by: user.id,
          updated_by: user.id,
          is_active: setActive,
        })
        .select()
        .single();

      if (error) {
        console.error('Create business error:', error);
        throw new Error('Failed to create business');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BUSINESSES_KEY] });
      queryClient.invalidateQueries({ queryKey: [BUSINESS_KEY] });
    },
  });
}
